import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const FinancingPage = () => {
  const options = [
    {
      icon: 'ri-bank-card-line',
      title: 'Solar Loans',
      description: 'Own your system with low-interest financing. Monthly payments often lower than your current electric bill.',
      features: ['0% down options', 'Competitive rates', 'Own your system', 'Maximize tax benefits'],
    },
    {
      icon: 'ri-hand-coin-line',
      title: 'Cash Purchase',
      description: 'Pay upfront and maximize your savings with the fastest ROI and full tax credit benefits.',
      features: ['Highest lifetime savings', 'Immediate ownership', 'Full tax credits', 'No interest charges'],
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Payment Plans',
      description: 'Flexible payment schedules designed to fit your budget and cash flow needs.',
      features: ['Customizable terms', 'Budget-friendly', 'Quick approval', 'No prepayment penalty'],
    },
  ];

  const incentives = [
    {
      title: 'Federal Solar Tax Credit (ITC)',
      amount: '30%',
      description: 'Deduct 30% of your total solar installation cost from your federal taxes. Available through 2032.',
    },
    {
      title: 'Florida Property Tax Exemption',
      amount: '100%',
      description: 'Solar installations are exempt from property tax assessments, even though they increase home value.',
    },
    {
      title: 'Net Metering',
      amount: 'Full Credit',
      description: 'Sell excess energy back to the grid at retail rates, maximizing your solar investment.',
    },
    {
      title: 'Accelerated Depreciation',
      amount: '85%',
      description: 'Commercial properties can depreciate 85% of system cost over 5 years (MACRS).',
    },
  ];

  return (
    <div className="font-poppins">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/6ebca9676ca9930ca264508be1d76bcf.jpg"
            alt="Affordable solar financing"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-solar-blue/80 via-solar-blue/60 to-solar-blue/80"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
          >
            Affordable Solar with<br />
            <span className="text-energy-yellow">Flexible Financing Options</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto"
          >
            Make solar energy affordable with payment plans designed to fit any budget. Start saving from day one.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link
              to="/contact"
              className="bg-energy-yellow text-solar-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-all shadow-2xl cursor-pointer whitespace-nowrap"
            >
              Get Your Custom Quote
            </Link>
            <a
              href="tel:352-509-6933"
              className="bg-white text-solar-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all shadow-2xl cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              352-509-6933
            </a>
          </motion.div>
        </div>
      </section>

      {/* Financing Options */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-4">
              Choose Your <strong>Payment Plan</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We offer multiple financing options to make solar energy accessible and affordable for everyone.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {options.map((option, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-soft-gray p-8 rounded-xl hover:shadow-2xl transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-energy-yellow rounded-lg mb-6">
                  <i className={`${option.icon} text-3xl text-solar-blue`}></i>
                </div>
                <h3 className="text-2xl font-bold text-solar-blue mb-3">{option.title}</h3>
                <p className="text-sm text-gray-600 mb-6">{option.description}</p>
                <ul className="space-y-3">
                  {option.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-700">
                      <i className="ri-check-line text-eco-green text-lg mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Comparison */}
      <section className="py-20 bg-soft-gray">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-4">
              Your Monthly Payment vs. <strong>Energy Bill</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              In most cases, your solar loan payment is less than your current electric bill, meaning immediate savings.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-lg"
              >
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-700 mb-2">Before Solar</h3>
                  <div className="text-5xl font-bold text-red-500 mb-4">$180</div>
                  <p className="text-sm text-gray-600">Average Monthly Electric Bill</p>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Electric Bill</span>
                    <span className="font-semibold text-gray-700">$180/mo</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Solar Payment</span>
                    <span className="font-semibold text-gray-700">$0/mo</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between">
                    <span className="font-bold text-gray-700">Total Monthly Cost</span>
                    <span className="font-bold text-red-500 text-xl">$180</span>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-eco-green/10 border-2 border-eco-green p-8 rounded-xl shadow-lg"
              >
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-700 mb-2">With Solar</h3>
                  <div className="text-5xl font-bold text-eco-green mb-4">$145</div>
                  <p className="text-sm text-gray-600">Average Monthly Total Cost</p>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Electric Bill</span>
                    <span className="font-semibold text-gray-700">$25/mo</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Solar Payment</span>
                    <span className="font-semibold text-gray-700">$120/mo</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between">
                    <span className="font-bold text-gray-700">Total Monthly Cost</span>
                    <span className="font-bold text-eco-green text-xl">$145</span>
                  </div>
                </div>
                <div className="mt-6 bg-energy-yellow/20 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-solar-blue">Save $35/mo</div>
                  <div className="text-sm text-gray-700">Immediate Monthly Savings</div>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 bg-white p-8 rounded-xl shadow-lg text-center"
            >
              <p className="text-lg text-gray-700 mb-4">
                <strong>After your loan is paid off</strong>, your electric bill drops to nearly $0 while traditional bills continue rising.
              </p>
              <div className="text-4xl font-bold text-solar-blue">$30,000+ in lifetime savings</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Incentives & Tax Credits */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-4">
              Maximize Your <strong>Incentives & Tax Credits</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Take advantage of federal, state, and local incentives that dramatically reduce your solar investment.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {incentives.map((incentive, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-soft-gray p-8 rounded-xl hover:shadow-xl transition-all"
              >
                <div className="flex items-start mb-4">
                  <div className="bg-energy-yellow text-solar-blue text-2xl font-bold px-4 py-2 rounded-lg mr-4">
                    {incentive.amount}
                  </div>
                  <h3 className="text-xl font-bold text-solar-blue pt-2">{incentive.title}</h3>
                </div>
                <p className="text-sm text-gray-600">{incentive.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Long-term Savings */}
      <section className="py-20 bg-solar-blue text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Long-Term <strong>Financial Benefits</strong>
              </h2>
              <p className="text-lg text-white/90 mb-8">
                Solar energy is one of the smartest financial decisions you can make. The savings compound over time, providing decades of financial benefits.
              </p>

              <div className="space-y-6">
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold">Years 1-10</span>
                    <span className="text-2xl font-bold text-energy-yellow">$12,000</span>
                  </div>
                  <p className="text-sm text-white/80">Savings during loan repayment period</p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold">Years 11-25</span>
                    <span className="text-2xl font-bold text-energy-yellow">$27,000</span>
                  </div>
                  <p className="text-sm text-white/80">Pure savings after loan is paid off</p>
                </div>

                <div className="bg-energy-yellow/20 border-2 border-energy-yellow p-6 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold">Total 25-Year Savings</span>
                    <span className="text-3xl font-bold text-energy-yellow">$39,000+</span>
                  </div>
                  <p className="text-sm text-white/90">Plus increased home value and environmental impact</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Successful%20homeowner%20celebrating%20financial%20savings%20and%20energy%20independence%20with%20solar%20panels%2C%20happy%20family%20in%20front%20of%20modern%20home%2C%20financial%20freedom%2C%20cost%20savings%20achievement%2C%20positive%20lifestyle%2C%20sustainable%20living%20success%2C%20bright%20sunny%20day%2C%20joyful%20atmosphere&width=800&height=1000&seq=financing-success&orientation=portrait"
                  alt="Financial success with solar"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Simple Approval Process */}
      <section className="py-20 bg-soft-gray">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-4">
              Simple <strong>Approval Process</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Getting approved for solar financing is quick and easy with our streamlined process.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-20 h-20 flex items-center justify-center bg-energy-yellow rounded-full mb-4 mx-auto">
                <i className="ri-file-list-3-line text-4xl text-solar-blue"></i>
              </div>
              <h3 className="text-lg font-bold text-solar-blue mb-2">1. Apply Online</h3>
              <p className="text-sm text-gray-600">Quick application takes just 5 minutes to complete</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="w-20 h-20 flex items-center justify-center bg-energy-yellow rounded-full mb-4 mx-auto">
                <i className="ri-time-line text-4xl text-solar-blue"></i>
              </div>
              <h3 className="text-lg font-bold text-solar-blue mb-2">2. Fast Approval</h3>
              <p className="text-sm text-gray-600">Get approved in as little as 24 hours</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="w-20 h-20 flex items-center justify-center bg-energy-yellow rounded-full mb-4 mx-auto">
                <i className="ri-draft-line text-4xl text-solar-blue"></i>
              </div>
              <h3 className="text-lg font-bold text-solar-blue mb-2">3. System Design</h3>
              <p className="text-sm text-gray-600">We design your custom solar solution</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <div className="w-20 h-20 flex items-center justify-center bg-energy-yellow rounded-full mb-4 mx-auto">
                <i className="ri-sun-line text-4xl text-solar-blue"></i>
              </div>
              <h3 className="text-lg font-bold text-solar-blue mb-2">4. Start Saving</h3>
              <p className="text-sm text-gray-600">Installation complete and savings begin</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-solar-blue mb-6">
              Ready to Make Solar Affordable?
            </h2>
            <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
              Let's discuss financing options and create a payment plan that works for your budget.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/contact"
                className="bg-energy-yellow text-solar-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-all shadow-xl cursor-pointer whitespace-nowrap"
              >
                Get Your Custom Quote
              </Link>
              <a
                href="tel:352-509-6933"
                className="bg-solar-blue text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-solar-blue/90 transition-all shadow-xl cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Call 352-509-6933
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FinancingPage;